import { Link, useLocation, useNavigate } from "react-router-dom";
import { Newspaper, BookOpen, LogIn, LogOut, User, Bell, BarChart3, Globe, Menu, Trophy, Eye } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/i18n/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { CURRENCIES } from "@/lib/currencies";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const NAV_ITEMS = [
  { to: "/news", label: "News", icon: Newspaper },
  { to: "/track-record", label: "Track Record", icon: Trophy },
  { to: "/watchlist", label: "Watchlist", icon: Eye },
  { to: "/blog", label: "Blog", icon: BookOpen },
];

const HIDDEN_ROUTES = ["/auth", "/checkout", "/payment-success", "/reset-password"];

const TopNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [currency, setCurrency] = useState("USD");
  const [sheetOpen, setSheetOpen] = useState(false);
  const { isAdmin } = useIsAdmin(user);

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("currency").eq("id", user.id).single().then(({ data }) => {
      if (data?.currency) setCurrency(data.currency);
    });
  }, [user]);

  if (HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r))) return null;

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const handleNav = (to: string) => {
    setSheetOpen(false);
    navigate(to);
  };

  return (
    <nav className="sticky top-0 z-40 border-b border-border/40 bg-background/85 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <Link to="/" className="font-display font-bold text-base sm:text-lg text-primary shrink-0">
          SignalGPT
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1 flex-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-display transition-colors whitespace-nowrap ${
                isActive(to) ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </Link>
          ))}
        </div>

        {/* Spacer on mobile */}
        <div className="flex-1 lg:hidden" />

        {/* Desktop right cluster */}
        <div className="hidden lg:flex items-center gap-1.5 shrink-0">
          {user ? (
            <>
              <button
                onClick={() => navigate("/alerts")}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gold/20 text-xs font-display text-gold hover:bg-gold/10 transition-colors"
                title="Alerts"
              >
                <Bell className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">Alerts</span>
              </button>
              <button
                onClick={() => navigate("/history")}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gold/20 text-xs font-display text-gold hover:bg-gold/10 transition-colors"
                title="History"
              >
                <BarChart3 className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">History</span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => navigate("/seo-admin")}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gold/20 text-xs font-display text-gold hover:bg-gold/10 transition-colors"
                  title="SEO Admin"
                >
                  <Globe className="w-3.5 h-3.5" />
                </button>
              )}
              <select
                value={currency}
                onChange={async (e) => {
                  const c = e.target.value;
                  setCurrency(c);
                  await supabase.from("profiles").update({ currency: c }).eq("id", user.id);
                }}
                className="hidden lg:block h-7 rounded-lg bg-card border border-border px-1.5 pr-5 text-xs font-display text-foreground focus:outline-none cursor-pointer max-w-[90px]"
                title="Currency"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code} className="bg-card text-foreground">{c.code} {c.symbol}</option>
                ))}
              </select>
              <LanguageSwitcher compact />
              <button
                onClick={() => navigate("/profile")}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-secondary/50 border border-border hover:bg-secondary/80 transition-colors"
                title="Profile"
              >
                <User className="w-3.5 h-3.5 text-gold" />
              </button>
              <button
                onClick={signOut}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-border text-xs font-display text-muted-foreground hover:text-foreground transition-colors"
                title="Sign out"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              <LanguageSwitcher compact />
              <button
                onClick={() => navigate("/auth")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/20 text-sm font-display text-gold hover:bg-gold/20 transition-colors"
              >
                <LogIn className="w-3.5 h-3.5" />
                {t("auth.signIn")}
              </button>
            </>
          )}
        </div>

        {/* Mobile right cluster */}
        <div className="flex lg:hidden items-center gap-1.5 shrink-0">
          {user ? (
            <button
              onClick={() => navigate("/profile")}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-secondary/50 border border-border"
              title="Profile"
              aria-label="Profile"
            >
              <User className="w-4 h-4 text-gold" />
            </button>
          ) : (
            <button
              onClick={() => navigate("/auth")}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gold/10 border border-gold/20 text-xs font-display text-gold"
            >
              <LogIn className="w-3.5 h-3.5" />
              {t("auth.signIn")}
            </button>
          )}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button
                className="flex items-center justify-center w-9 h-9 rounded-lg border border-border bg-card/80"
                aria-label="Open menu"
              >
                <Menu className="w-4 h-4 text-foreground" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col">
              <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/40">
                <SheetTitle className="font-display text-primary">SignalGPT</SheetTitle>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                  <button
                    key={to}
                    onClick={() => handleNav(to)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-display transition-colors ${
                      isActive(to) ? "bg-primary/15 text-primary" : "text-foreground hover:bg-secondary/60"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}

                {user && (
                  <>
                    <div className="h-px bg-border/40 my-2" />
                    <button
                      onClick={() => handleNav("/alerts")}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-display text-foreground hover:bg-secondary/60 transition-colors"
                    >
                      <Bell className="w-4 h-4 text-gold" />
                      Alerts
                    </button>
                    <button
                      onClick={() => handleNav("/history")}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-display text-foreground hover:bg-secondary/60 transition-colors"
                    >
                      <BarChart3 className="w-4 h-4 text-gold" />
                      History
                    </button>
                    <button
                      onClick={() => handleNav("/profile")}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-display text-foreground hover:bg-secondary/60 transition-colors"
                    >
                      <User className="w-4 h-4 text-gold" />
                      Profile
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleNav("/seo-admin")}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-display text-foreground hover:bg-secondary/60 transition-colors"
                      >
                        <Globe className="w-4 h-4 text-gold" />
                        SEO Admin
                      </button>
                    )}
                  </>
                )}

                <div className="h-px bg-border/40 my-2" />

                <div className="px-3 py-2">
                  <div className="text-xs text-muted-foreground font-display mb-2">Language</div>
                  <LanguageSwitcher />
                </div>

                {user && (
                  <div className="px-3 py-2">
                    <div className="text-xs text-muted-foreground font-display mb-2">Currency</div>
                    <select
                      value={currency}
                      onChange={async (e) => {
                        const c = e.target.value;
                        setCurrency(c);
                        await supabase.from("profiles").update({ currency: c }).eq("id", user.id);
                      }}
                      className="w-full h-9 rounded-lg bg-card border border-border px-2 text-sm font-display text-foreground focus:outline-none"
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c.code} value={c.code} className="bg-card text-foreground">{c.code} {c.symbol}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {user && (
                <div className="border-t border-border/40 p-3">
                  <SheetClose asChild>
                    <button
                      onClick={signOut}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-border text-sm font-display text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </SheetClose>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default TopNav;
